// src/components/expenses/ExpensesMobileView.tsx
import React from 'react';
import { Edit, Trash2, Search, RefreshCw } from 'lucide-react';
import { ExpenseData } from '../../pages/Expenses'; // Adjust path
import Pagination from '../shared/Pagination'; // Adjust path to your shared pagination component

interface ExpensesMobileViewProps {
  paginatedData: ExpenseData[];
  apiLoading: boolean;
  searchTerm: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleEdit: (expense: ExpenseData) => void;
  handleDelete: (id: string) => void;
  getLabel: (group: string, value: string) => string;
  formatCurrency: (value: string | number) => string;
  paginationProps: any; // Pass all pagination props in a single object
}

const ExpenseCard: React.FC<Omit<ExpensesMobileViewProps, 'paginatedData' | 'apiLoading' | 'searchTerm' | 'onSearchChange' | 'paginationProps'> & { expense: ExpenseData }> = ({
    expense, handleEdit, handleDelete, getLabel, formatCurrency
}) => (
    <div key={expense.id} className="bg-white rounded-lg p-3 shadow border border-gray-200 hover:shadow-md transition-all duration-200 flex flex-col">
        <div className="flex justify-between items-start mb-2">
            <div className="text-amber-800 font-bold truncate text-sm">
                {getLabel('expenseTypes', expense.expenseType) || 'Expense'}
            </div>
            <div className="flex space-x-1">
                <button onClick={() => handleEdit(expense)} className="p-1 text-amber-600 hover:bg-amber-50 rounded transition-colors"><Edit className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(expense.id)} className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
        </div>
        <div className="mb-2">
            <p className="text-base font-bold text-amber-600 text-center truncate">{formatCurrency(expense.expenseAmount)}</p>
        </div>
        <div className="space-y-1 text-xs text-gray-600 text-center">
            <div className="text-gray-800 font-medium truncate">{expense.expenseName}</div>
            {expense.expenseSubType && <div className="text-gray-800 font-medium truncate">{getLabel('expenseSubTypes', expense.expenseSubType)}</div>}
            <div className="text-gray-800 font-medium">{getLabel('expenseFrequencies', expense.expenseFrequency)}</div>
        </div>
    </div>
);

const ExpensesMobileView: React.FC<ExpensesMobileViewProps> = ({ paginatedData, apiLoading, searchTerm, onSearchChange, paginationProps, ...rest }) => {
    return (
        <div className="md:hidden">
            <div className="p-2">
                <div className="relative max-w-md mx-auto mb-4">
                    <input type="text" placeholder="Search expense records..." value={searchTerm} onChange={onSearchChange} className="w-full p-2 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-600" />
                    <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
            </div>
            {apiLoading ? (
                <div className="text-center py-12 flex justify-center items-center"><RefreshCw className="w-6 h-6 animate-spin text-amber-600" /><span className="ml-2">Loading...</span></div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 max-w-full px-2">
                    {paginatedData.map(expense => <ExpenseCard key={expense.id} expense={expense} {...rest} />)}
                </div>
            )}
            {paginationProps.totalItems > paginationProps.itemsPerPage && !apiLoading && (
                <Pagination {...paginationProps} isMobile />
            )}
        </div>
    );
};

export default ExpensesMobileView;